import React, { MouseEvent, memo } from "react";
import { twMerge } from "tailwind-merge";

import { ComponentEventProperties, addTrackingToEvent } from "utils/amplitude";
import { Link, type To } from "utils/routing";

type DataAttributes = Record<`data-${string}`, string | undefined>;

export interface ListItemProps extends ComponentEventProperties {
  kind?: "primary" | "secondary";
  isSelected?: boolean;
  isUnreleased?: boolean;
  selectedColoring?: "selected-blue" | "selected-green" | "selected-orange";
  className?: string;
  children?: React.ReactNode;
  label?: string;
  to?: To;
  onClick?: (event: MouseEvent) => void;
  grabbable?: boolean;
  lastItem?: boolean;
  title?: string;
}

const KIND_CLASSES: Record<string, string> = {
  primary: "min-h-[45px] px-3 gap-2 border-t border-black-tint-90",
  secondary: "h-[36px] py-[2px] pr-1 pl-2 my-[2px] rounded-md",
};

const SELECTED_COLORING_CLASSES: Record<string, string> = {
  "selected-blue": "data-[selected]:bg-blue data-[selected]:text-blue-tint-90",
  "selected-green": "data-[selected]:bg-green-shade-20 data-[selected]:text-green-tint-90",
  "selected-orange": "data-[selected]:bg-orange data-[selected]:text-orange-tint-90",
};

/**
 * A ListItem provides the basic styling for single item in a list or navigation menu.
 */
export const ListItem = memo(function ListItemInternal({
  kind = "secondary",
  isSelected = false,
  isUnreleased = false,
  selectedColoring = "selected-blue",
  lastItem = false,
  className: customClassName,
  children,
  label,
  to,
  onClick,
  eventName,
  eventProperties,
  grabbable,
  title,
}: ListItemProps) {
  const className = twMerge(
    "group/listitem flex min-w-12 max-w-full items-center self-stretch truncate text-black-tint-20 hover:bg-black-tint-97 data-[unreleased]:text-black-tint-55",
    KIND_CLASSES[kind] || "",
    SELECTED_COLORING_CLASSES[selectedColoring] || "",
    kind === "primary" && lastItem ? "border-b" : "",
    grabbable
      ? "cursor-grab active:cursor-grabbing"
      : onClick
        ? "cursor-pointer"
        : "cursor-default",
    customClassName,
  );

  const clickHandler = addTrackingToEvent({ eventName, eventProperties, onEvent: onClick });

  const props: React.HTMLAttributes<HTMLLIElement> & DataAttributes = {
    "aria-current": isSelected ? "true" : undefined,
    "aria-label": label,
    "data-unreleased": isUnreleased ? "true" : undefined,
    "data-selected": isSelected ? "true" : undefined,
    onClick: clickHandler,
    className,
    title,
  };

  if (to) {
    return (
      <li {...props}>
        <Link
          className="flex w-full min-w-12 items-center text-[inherit] hover:text-[inherit]"
          draggable={false}
          to={to}
        >
          {children}
        </Link>
      </li>
    );
  }
  return <li {...props}>{children}</li>;
});
